import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Stack,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import Api from "../../Services/Api";

const PublishFeedModal = ({
    open,
    onClose,
    selectedFeedId,
    feeds,
    setFeeds,
    keywordLists,
    fetchKeywordsList,
    refreshFeeds,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen
}) => {
    const [editingGeneralInfo, setEditingGeneralInfo] = useState(false);
    const [selectedKeywordsSet, setSelectedKeywordsSet] = useState('');
    const [showNewKeywordsForm, setShowNewKeywordsForm] = useState(false);
    const [newKeywordsInput, setNewKeywordsInput] = useState('');
    const [newKeywordsName, setNewKeywordsName] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);
    const [keywordSearch, setKeywordSearch] = useState('');
    const [sortMode, setSortMode] = useState('none'); // 'none', 'asc', 'desc'
    const [randomSelected, setRandomSelected] = useState(false);
    const keywordSelectRef = useRef(null);
    const [selectOpen, setSelectOpen] = useState(false);
    
    const capOptions = [1000, 3000, 5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000];
    
    const handleClose = () => {
        setEditingGeneralInfo(false);
        setSelectedKeywordsSet('');
        setShowNewKeywordsForm(false);
        setNewKeywordsInput('');
        setNewKeywordsName('');
        setKeywordSearch('');
        setSortMode('none');
        onClose();
    };

    const cleanKeywords = (keywords) => {
        if (typeof keywords !== 'string') return [];
        
        // Split by commas, newlines, or pipes, and clean each keyword
        const cleanedArray = keywords
            .split(/[,\n|]+/)  // split by comma, newline, or pipe
            .map(keyword => keyword.trim())
            .filter(keyword => keyword.length > 0)
            .map(keyword => keyword.replace(/[^a-zA-Z0-9\s]/g, '')); // keep spaces

        return cleanedArray;
    };

    const handleCreateAndSelectKeywords = async () => {
        try {
            const cleanedKeywords = cleanKeywords(newKeywordsInput);
            
            // Don't submit if there are no valid keywords after cleaning
            if (!cleanedKeywords || cleanedKeywords.length === 0) {
                setSnackbarMessage('Please enter valid keywords (alphanumeric characters only, minimum 2 characters)');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            const response = await Api.createKeywordList({
                keywords: cleanedKeywords,  // send as array
                name: newKeywordsName
            });

            if (response && response.data) {
                setSelectedKeywordsSet(response.data.id);
                await fetchKeywordsList();
                setShowNewKeywordsForm(false);
                setNewKeywordsInput('');
                setNewKeywordsName('');
                setSnackbarMessage('Keywords list created successfully');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error creating keywords:', error);
            if (error && error.data) {
                const { message, errors } = error.data;
                if (errors) {
                    setSnackbarMessage(Object.values(errors).flat().join('. '));
                } else {
                    setSnackbarMessage(message || 'Failed to create keywords list');
                }
            } else {
                setSnackbarMessage('Failed to create keywords list');
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleSubmitPublish = async () => {
        if (isPublishing) return; // Prevent multiple calls
        setIsPublishing(true); // Set flag to true

        // Ensure feeds is an array and find the feed
        const feed = Array.isArray(feeds) ? feeds.find((feed) => feed.id === selectedFeedId) : null;

        if (!feed) {
            setSnackbarMessage('Feed not found.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setIsPublishing(false);
            return;
        }

        try {
            await Api.publishSpinSupplyFeed(selectedFeedId, {
                spin_id: selectedFeedId,
                keyword_list_id: selectedKeywordsSet,
                link: feed.link,
                identifier: feed.identifier,
                country: feed.country,
                source: feed.source,
                device: feed.device,
                capping: feed.cap,
                type: feed.type,
                notes: feed.notes, // Include notes in the request
            });

            setSnackbarMessage(`Feed ${selectedFeedId} has been published successfully.`);
            setSnackbarSeverity('success');
            handleClose(); // Close modal after publishing
            refreshFeeds(); // Refresh the table after publishing
        } catch (error) {
            console.error('Publish Error:', error);
            setSnackbarMessage('Failed to publish feed. Please try again.');
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true);
            setIsPublishing(false); // Reset the flag when the function completes
        }
    };

    // Toggle sort mode: none -> desc -> asc -> none
    const toggleSortMode = () => {
        if (sortMode === 'none') setSortMode('desc');
        else if (sortMode === 'desc') setSortMode('asc');
        else setSortMode('none');
    };

    // Select a random list from top 20 RPM lists
    const selectRandomTopList = (event) => {
        // Prevent the dropdown from reopening when clicking the button
        event.stopPropagation();
        
        // Sort the lists by RPM in descending order
        const sortedLists = [...(keywordLists || [])].sort((a, b) => {
            const rpmA = a.stats?.last7days?.rpm || 0;
            const rpmB = b.stats?.last7days?.rpm || 0;
            return rpmB - rpmA;
        });
        
        // Get the top 20 lists (or whatever is available if less than 20)
        const topLists = sortedLists.slice(0, Math.min(20, sortedLists.length));
        
        if (topLists.length === 0) {
            setSnackbarMessage('No keyword lists available to choose from');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }
        
        // Select a random list from the top lists
        const randomIndex = Math.floor(Math.random() * topLists.length);
        const randomList = topLists[randomIndex];
        
        setSelectedKeywordsSet(randomList.id);
        setRandomSelected(true);
        
        // Show a success message
        setSnackbarMessage(`Randomly selected "${randomList.name}" from top ${topLists.length} RPM lists`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Close the dropdown
        setSelectOpen(false);
        
        // Clear random flag after a timeout
        setTimeout(() => setRandomSelected(false), 3000);
    };

    // Filter keyword lists based on search
    const filteredKeywordLists = (keywordLists || [])
        .filter(list => 
            list.name.toLowerCase().includes(keywordSearch.toLowerCase())
        )
        .sort((a, b) => {
            if (sortMode === 'none') return 0;
            
            const rpmA = a.stats?.last7days?.rpm || 0;
            const rpmB = b.stats?.last7days?.rpm || 0;
            
            return sortMode === 'desc' ? rpmB - rpmA : rpmA - rpmB;
        });

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Publish Feed</DialogTitle>
            <DialogContent>
                <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant={"h6"}>Review the Info</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setEditingGeneralInfo(!editingGeneralInfo)}
                        >
                            {editingGeneralInfo ? "Cancel Edit" : "Edit"}
                        </Button>
                    </Stack>

                    {editingGeneralInfo ? (
                        <>
                            <TextField
                                label="Supply Feed Link"
                                value={feeds.find((feed) => feed.id === selectedFeedId)?.link || ''}
                                onChange={(e) => {
                                    const updatedFeeds = feeds.map((feed) =>
                                        feed.id === selectedFeedId ? { ...feed, link: e.target.value } : feed
                                    );
                                    setFeeds(updatedFeeds);
                                }}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{ mt: 2 }}
                                placeholder="Enter the correct URL (must contain {q} for keyword macros)"
                            />

                            <TextField
                                label="Identifier"
                                value={feeds.find((feed) => feed.id === selectedFeedId)?.identifier || ''}
                                onChange={(e) => {
                                    const updatedFeeds = feeds.map((feed) =>
                                        feed.id === selectedFeedId ? { ...feed, identifier: e.target.value } : feed
                                    );
                                    setFeeds(updatedFeeds);
                                }}
                                fullWidth
                                sx={{ mt: 2 }}
                            />

                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>Source</InputLabel>
                                <Select
                                    value={feeds.find((feed) => feed.id === selectedFeedId)?.source || ''}
                                    onChange={(e) => {
                                        const updatedFeeds = feeds.map((feed) =>
                                            feed.id === selectedFeedId ? { ...feed, source: e.target.value } : feed
                                        );
                                        setFeeds(updatedFeeds);
                                    }}
                                    label="Source"
                                >
                                    {['Taboola', 'Outbrain', 'Google', 'Facebook', 'Bing'].map((source) => (
                                        <MenuItem key={source} value={source.toLowerCase()}>
                                            {source}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>Cap (Searches per Day)</InputLabel>
                                <Select
                                    value={feeds.find((feed) => feed.id === selectedFeedId)?.cap || ''}
                                    onChange={(e) => {
                                        const updatedFeeds = feeds.map((feed) =>
                                            feed.id === selectedFeedId ? { ...feed, cap: e.target.value } : feed
                                        );
                                        setFeeds(updatedFeeds);
                                    }}
                                    label="Cap (Searches per Day)"
                                >
                                    {capOptions.map((cap) => (
                                        <MenuItem key={cap} value={cap}>
                                            {cap.toLocaleString()} searches/day
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Notes"
                                value={feeds.find((feed) => feed.id === selectedFeedId)?.notes || ''}
                                onChange={(e) => {
                                    const updatedFeeds = feeds.map((feed) =>
                                        feed.id === selectedFeedId ? { ...feed, notes: e.target.value } : feed
                                    );
                                    setFeeds(updatedFeeds);
                                }}
                                fullWidth
                                sx={{ mt: 2 }}
                                placeholder="Edit any notes for the feed"
                            />
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Supply Feed Link:</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ wordWrap: 'break-word', mb: 2 }}>
                                {feeds.find((feed) => feed.id === selectedFeedId)?.link || 'N/A'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <strong>Identifier:</strong>
                            </Typography>

                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {feeds.find((feed) => feed.id === selectedFeedId)?.identifier || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Device:</strong>
                            </Typography>

                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {feeds.find((feed) => feed.id === selectedFeedId)?.device || 'N/A'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <strong>Country:</strong>
                            </Typography>

                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {feeds.find((feed) => feed.id === selectedFeedId)?.country || 'N/A'}
                            </Typography>


                            <Typography variant="body2" color="text.secondary">
                                <strong>Source:</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {feeds.find((feed) => feed.id === selectedFeedId)?.source?.toUpperCase() || 'N/A'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <strong>Cap (Searches per Day):</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {feeds.find((feed) => feed.id === selectedFeedId)?.cap?.toLocaleString() || 'N/A'} searches/day
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <strong>Notes</strong>
                            </Typography>
                            <Typography variant="body1">
                                {feeds.find((feed) => feed.id === selectedFeedId)?.notes || 'N/A'}
                            </Typography>
                        </>
                    )}
                </Paper>

                <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
                    Keywords List:
                </Typography>

                {!showNewKeywordsForm ? (
                    <>
                        <FormControl fullWidth>
                            <Select
                                ref={keywordSelectRef}
                                value={selectedKeywordsSet}
                                onChange={(e) => setSelectedKeywordsSet(e.target.value)}
                                displayEmpty
                                open={selectOpen}
                                onOpen={() => setSelectOpen(true)}
                                onClose={() => setSelectOpen(false)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400,
                                        },
                                    },
                                    MenuListProps: {
                                        subheader: (
                                            <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <TextField
                                                        placeholder="Search keyword lists..."
                                                        value={keywordSearch}
                                                        onChange={(e) => setKeywordSearch(e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        variant="outlined"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    />
                                                    <Tooltip title={sortMode === 'none' 
                                                        ? "Sort by RPM" 
                                                        : sortMode === 'desc' 
                                                            ? "Sort by highest RPM" 
                                                            : "Sort by lowest RPM"}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleSortMode();
                                                            }}
                                                            sx={{ ml: 1 }}
                                                        >
                                                            {sortMode === 'none' && <SortIcon />}
                                                            {sortMode === 'desc' && <ArrowDownwardIcon />}
                                                            {sortMode === 'asc' && <ArrowUpwardIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Pick random from top 20 RPM lists">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={selectRandomTopList}
                                                            sx={{ ml: 0.5 }}
                                                            color={randomSelected ? "primary" : "default"}
                                                        >
                                                            <ShuffleIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                {sortMode !== 'none' && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Chip 
                                                            size="small" 
                                                            label={`Sorted by ${sortMode === 'desc' ? 'highest' : 'lowest'} RPM`} 
                                                            onDelete={() => setSortMode('none')}
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        ),
                                    },
                                }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <em>Select a keyword list</em>;
                                    }
                                    
                                    const selectedItem = keywordLists.find(item => item.id === selected);
                                    return selectedItem ? selectedItem.name : <em>Select a keyword list</em>;
                                }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {filteredKeywordLists.map((set) => (
                                    <MenuItem key={set.id} value={set.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <Typography variant="body1">{set.name}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                <Chip 
                                                    size="small" 
                                                    label={`${set.keywords?.length || 0} keywords`} 
                                                    variant="outlined" 
                                                />
                                                <Chip 
                                                    size="small" 
                                                    label={`${set.stats?.last7days?.searches?.toLocaleString() || 0} searches`} 
                                                    variant="outlined" 
                                                />
                                                <Chip 
                                                    size="small" 
                                                    label={`$${set.stats?.last7days?.rpm?.toFixed(2) || '0.00'} RPM`} 
                                                    variant="outlined" 
                                                />
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setShowNewKeywordsForm(true)}
                            sx={{ mt: 2 }}
                        >
                            Create New Keywords List
                        </Button>
                    </>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="List Name"
                            value={newKeywordsName}
                            onChange={(e) => setNewKeywordsName(e.target.value)}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label="Keywords"
                            value={newKeywordsInput}
                            onChange={(e) => setNewKeywordsInput(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                            helperText="Enter keywords separated by commas, spaces, or new lines"
                        />
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleCreateAndSelectKeywords}
                                disabled={!newKeywordsName.trim() || !newKeywordsInput.trim()}
                            >
                                Create and Select
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowNewKeywordsForm(false);
                                    setNewKeywordsInput('');
                                    setNewKeywordsName('');
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmitPublish} 
                    variant="contained" 
                    color="primary"
                    disabled={isPublishing}
                >
                    {isPublishing ? 'Publishing...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PublishFeedModal; 